/**
 * DateRangePicker Component
 * A clean popover-based date range picker with start and end date selection
 */

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** YYYY-MM-DD in local time. toISOString() shifts the calendar day west of UTC. */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Parse a DATE from the API. node-pg + res.json turns Postgres DATE into
 * `2026-03-01T00:00:00.000Z`; appending T00:00:00 to that is Invalid Date.
 * Always take the first 10 chars and interpret as local midnight.
 */
export function parseLocalDateOnly(value?: Date | string | null): Date | undefined {
  if (value == null || value === "") return undefined;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }
  const dateOnly = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return undefined;
  const parsed = new Date(`${dateOnly}T00:00:00`);
  return isNaN(parsed.getTime()) ? undefined : parsed;
}

interface DateRangePickerProps {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  onStartDateChange?: (date: Date | undefined) => void;
  onEndDateChange?: (date: Date | undefined) => void;
  minDate?: Date;
  maxStartDate?: Date;
  maxEndDate?: Date;
  disabled?: boolean;
  startLabel?: string;
  endLabel?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  className?: string;
}

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxStartDate,
  maxEndDate,
  disabled = false,
  startLabel = "Start Date",
  endLabel = "End Date",
  startPlaceholder = "mm/dd/yyyy",
  endPlaceholder = "mm/dd/yyyy",
  className,
}: DateRangePickerProps) {
  const [startOpen, setStartOpen] = React.useState(false);
  const [endOpen, setEndOpen] = React.useState(false);

  const validStartDate = parseLocalDateOnly(startDate);
  const validEndDate = parseLocalDateOnly(endDate);

  const handleStartSelect = (date: Date | undefined) => {
    onStartDateChange?.(date);
    setStartOpen(false);
  };

  const handleEndSelect = (date: Date | undefined) => {
    onEndDateChange?.(date);
    setEndOpen(false);
  };

  return (
    <div className={cn("flex gap-4", className)}>
      {/* Start Date */}
      <div className="flex-1">
        <label className="text-sm font-medium text-foreground mb-2 block">{startLabel}</label>
        <Popover open={startOpen} onOpenChange={setStartOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-between text-left font-normal h-11 rounded-lg border border-border",
                !validStartDate && "text-muted-foreground"
              )}
            >
              {validStartDate ? format(validStartDate, "MM/dd/yyyy") : <span>{startPlaceholder}</span>}
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={validStartDate}
              onSelect={handleStartSelect}
              defaultMonth={validStartDate || minDate || new Date(new Date().getFullYear(), 0, 1)}
              disabled={(date) => {
                if (minDate && date < minDate) return true;
                if (maxStartDate && date > maxStartDate) return true;
                return false;
              }}
              fixedWeeks
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* End Date */}
      <div className="flex-1">
        <label className="text-sm font-medium text-foreground mb-2 block">{endLabel}</label>
        <Popover open={endOpen} onOpenChange={setEndOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-between text-left font-normal h-11 rounded-lg border border-border",
                !validEndDate && "text-muted-foreground"
              )}
            >
              {validEndDate ? format(validEndDate, "MM/dd/yyyy") : <span>{endPlaceholder}</span>}
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={validEndDate}
              onSelect={handleEndSelect}
              defaultMonth={validEndDate || validStartDate || minDate || new Date(new Date().getFullYear(), 0, 1)}
              disabled={(date) => {
                // Can't select before start date
                if (validStartDate && date < validStartDate) return true;
                if (minDate && date < minDate) return true;
                if (maxEndDate && date > maxEndDate) return true;
                return false;
              }}
              fixedWeeks
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
